import { Preferences } from 'components/pages/Home/types/Preferences';
import { LeaseFormType } from 'components/pages/Leases/LeaseForm/form/LeaseForm';
import Snackbar from 'components/shared/Toaster/ToasterWithoutState';
import { TBasePaths } from 'constants/translations';
import { LeaseEntity } from 'entityTypes/Lease/Lease';
import { LeaseSpaceEntity } from 'entityTypes/Lease/LeaseSpace';
import { t } from 'i18next';
import { SetterOrUpdater } from 'recoil';
import { UpdateLeaseSpaceDto } from 'types/lease-space';
import logger from 'utils/logger';

import { LeaseSpaceTermEntity } from 'entityTypes/Lease/LeaseSpaceTerm';
import { groupBy, isEqual, pickBy } from 'lodash';
import { notEmpty } from './filters';
import { convertCurrency } from './unitConverters';

/**
 * Handles errors gracefully and logs them before returning a fallback value.
 * @param error The caught error or unknown type
 * @param fallbackValue The value to return in case of error
 * @param initiator The value that represents the function name to track
 * @returns The fallback value provided
 */
const handleCatchAndLog = <T>(error: unknown, fallbackValue: T, initiator: string): T => {
  const err = error as Error;
  logger(err, initiator);
  Snackbar.warning(err.message);
  return fallbackValue;
};

export const convertAreaIfNeeded = (
  rentArea: number,
  rentAreaUom: string,
  desiredUom: string
): number => {
  if (rentAreaUom !== desiredUom) {
    return rentAreaUom === 'sqm' ? rentArea * 10.76391042 : rentArea / 10.76391042;
  }
  return rentArea;
};

export const getCurrentRelevantLeaseSpaces = (
  leaseSpaces: LeaseSpaceEntity[]
): LeaseSpaceEntity[] => {
  const { active = [], upcoming = [], expired = [] } = groupBy(leaseSpaces, 'leaseSpaceStatus');

  return active.length ? active : upcoming.length ? upcoming : expired;
};

export const calculateAnnualCostPerAreaWithoutTax = (
  lease: LeaseEntity,
  preferences: Preferences
): number | undefined => {
  if (!lease.costSummary) return;

  const totalRentArea = convertAreaIfNeeded(lease.rentArea ?? 0, lease.unitMeas, preferences.uom);
  return totalRentArea && totalRentArea != 0
    ? convertCurrency(
        preferences,
        lease.costSummary?.annualCostWithoutTax / totalRentArea,
        lease.currencyCode
      )
    : undefined;
};

export const concatTotalFloorSuitSpaces = (leaseSpaces: LeaseSpaceEntity[]): string => {
  try {
    const filtered = getCurrentRelevantLeaseSpaces(leaseSpaces)
      .map((ls) => ls.floorSuiteSpace)
      .filter(notEmpty);
    filtered.sort((a, b) => a.localeCompare(b));
    return filtered.join('; ');
  } catch (e) {
    return handleCatchAndLog(e, '0', 'concatTotalFloorSuitSpaces');
  }
};

export const hasMultipleLeaseSpaces = (leaseSpaces: LeaseSpaceEntity[]): boolean => {
  try {
    return leaseSpaces.length > 1;
  } catch (e) {
    return handleCatchAndLog(e, false, 'hasMultipleLeaseSpaces');
  }
};

export const anySpaceHasMultipleTerms = (leaseSpaces: LeaseSpaceEntity[]): boolean => {
  if (!leaseSpaces.length) return false;
  return leaseSpaces.some((ls) => (ls.leaseSpaceTerms?.length ?? 0) > 1);
};

export const findRelatedLeaseSpace = (
  leaseSpaces: LeaseSpaceEntity[],
  lease: LeaseEntity | null
): LeaseSpaceEntity | undefined | null => {
  try {
    return (leaseSpaces.find((s) => s.leaseSpaceUuid === lease?.leaseUuid) ?? leaseSpaces.length)
      ? leaseSpaces[0]
      : null;
  } catch (e) {
    return handleCatchAndLog(e, undefined, 'findRelatedLeaseSpace');
  }
};

export const updateLeaseSpaceIfNeeded = async (
  updatedValues: Partial<LeaseFormType>,
  leaseSpaces: LeaseSpaceEntity[],
  lease: LeaseEntity | null,
  onUpdateLeaseSpace: (dto: UpdateLeaseSpaceDto) => Promise<LeaseSpaceEntity>,
  onSetLeaseSpacesState: SetterOrUpdater<LeaseSpaceEntity[]>
) => {
  const relatedLeaseSpace = findRelatedLeaseSpace(leaseSpaces, lease);
  const leaseSpaceUuid = relatedLeaseSpace?.leaseSpaceUuid;
  if (!leaseSpaceUuid) return Snackbar.warning(t(`${TBasePaths.PA_COMMON_WORD}.uuidNotProvided`));

  const changedData = pickBy(
    updatedValues,
    (value: unknown, key: keyof LeaseSpaceEntity) => !isEqual(value, relatedLeaseSpace[key])
  ) as Partial<LeaseSpaceEntity>;

  if (Object.keys(changedData).length) {
    changedData.leaseSpaceUuid = leaseSpaceUuid;
    await onUpdateLeaseSpace(changedData);

    onSetLeaseSpacesState((prev) =>
      prev.map((p) => (p.leaseSpaceUuid === leaseSpaceUuid ? { ...p, ...changedData } : p))
    );
  }
};

export const shouldUpdateLeaseSpaceTerm = (
  updatedTerm: Partial<LeaseSpaceTermEntity>,
  originalTerm: Partial<LeaseSpaceTermEntity>
) => {
  const changedData = pickBy(
    updatedTerm,
    (value: unknown, key: keyof LeaseSpaceTermEntity) => !isEqual(value, originalTerm[key])
  ) as Partial<LeaseSpaceTermEntity>;

  return Object.keys(changedData).length > 0;
};

export const sortSpaceTerms = (leaseSpace: LeaseSpaceEntity) => {
  return (
    [...(leaseSpace.leaseSpaceTerms ?? [])].sort((a, b) => {
      const aDate = a.areaChangeDate
        ? new Date(a.areaChangeDate)
        : a.commenceDate
          ? new Date(a.commenceDate)
          : null;
      const bDate = b.areaChangeDate
        ? new Date(b.areaChangeDate)
        : b.commenceDate
          ? new Date(b.commenceDate)
          : null;

      return aDate ? (bDate ? aDate.getTime() - bDate.getTime() : 1) : -1;
    }) ?? []
  );
};

export const areDatesOverlappingInRenewals = (
  dates: { commenceDate: string | Date | null; expireDate: string | Date | null },
  terms: LeaseSpaceTermEntity[]
) => {
  const commenceDate = dates.commenceDate ? new Date(dates.commenceDate) : null;
  const expireDate = dates.expireDate ? new Date(dates.expireDate) : null;
  return terms
    .filter((t) => !t.areaChangeDate)
    .some((t) => {
      const tCommenceDate = t.commenceDate ? new Date(t.commenceDate) : null;
      const tExpireDate = t.expireDate ? new Date(t.expireDate) : null;

      // Handle open-ended ranges manually, ranges overlap if they don't clearly NOT overlap
      const termStartsBeforeInputEnd = !expireDate || !tCommenceDate || tCommenceDate <= expireDate;
      const termEndsAfterInputStart = !commenceDate || !tExpireDate || tExpireDate >= commenceDate;

      return termStartsBeforeInputEnd && termEndsAfterInputStart;
    });
};

export const hasExpirationDateForAllRenewals = (leaseSpace: LeaseSpaceEntity) => {
  return (
    leaseSpace.leaseSpaceTerms?.filter((t) => !t.areaChangeDate).every((t) => t.expireDate) ?? false
  );
};

export const hasSingleRenewal = (leaseSpace: LeaseSpaceEntity) => {
  return leaseSpace.leaseSpaceTerms?.filter((t) => !t.areaChangeDate).length === 1;
};

export const hasSingleAreaChange = (leaseSpace: LeaseSpaceEntity) => {
  return leaseSpace.leaseSpaceTerms?.filter((t) => t.rentArea !== null).length === 1;
};

export const listOfLeaseSpaceFields: (keyof LeaseSpaceEntity)[] = [
  'floorSuiteSpace',
  'leaseSpaceStatus',
  'leaseSpaceNote',
  'leaseAreaTypeUuid',
  'excludeBudget',
];

export const listOfLeaseSpaceTermFields: (keyof LeaseSpaceTermEntity)[] = [
  'rentArea',
  'usableArea',
  'unitCnt',
  'commenceDate',
  'expireDate',
  'expireReviewDate',
  'areaChangeDate',
  'executionDate',
  'isMonthToMonth',
  'note',
];
